/**
 * KOAI Chat Page
 * AI 채팅 페이지 (팀 주제방 포함)
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Chip,
  Button,
} from '@mui/material';
import { 
  SmartToy as BotIcon,
  Business as BusinessIcon,
  Folder as FolderIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';

import ChatMessage from '../components/chat/ChatMessage';
import ChatInput from '../components/chat/ChatInput';
import SessionList from '../components/chat/SessionList';
import EphemeralFileUpload from '../components/chat/EphemeralFileUpload';
import { ragAPI, teamTopicsAPI, teamsAPI, ephemeralAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useChatSocket } from '../hooks/useSocket';

const lastSessionStorageKey = (userId) =>
  userId ? `lastChatSessionId:${userId}` : 'lastChatSessionId';

export default function ChatPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // 팀 주제방 상태
  const [teamTopics, setTeamTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [expandedTeams, setExpandedTeams] = useState({});
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamSessions, setTeamSessions] = useState({}); // 팀별 세션 목록 { teamId: [sessions] }
  const [chartData, setChartData] = useState(null); // 차트 데이터
  
  // Ephemeral RAG 상태
  const [ephemeralSessionId, setEphemeralSessionId] = useState(null);
  const [ephemeralDocuments, setEphemeralDocuments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');

  const rememberSessionId = (sessionId) => {
    if (!sessionId) return;
    localStorage.setItem(lastSessionStorageKey(user?.id), String(sessionId));
    // 레거시 키 정리 (계정 간 혼선 방지)
    localStorage.removeItem('lastChatSessionId');
  };

  const readRememberedSessionId = () => {
    const scoped = localStorage.getItem(lastSessionStorageKey(user?.id));
    if (scoped) return scoped;
    // 마이그레이션: 구 키가 현재 사용자 목록에 있으면 승격
    return localStorage.getItem('lastChatSessionId');
  };

  const clearRememberedSessionId = () => {
    localStorage.removeItem(lastSessionStorageKey(user?.id));
    localStorage.removeItem('lastChatSessionId');
  };
  
  // 소켓 연결
  const { 
    isConnected, 
    isStreaming, 
    joinSession, 
    leaveSession, 
    sendMessage, 
    onChunk, 
    onComplete, 
    onError,
    onChart,
    onLimit,
    onStatus,
    reconnect
  } = useChatSocket();

  // 로그인 상태 변경 시 소켓 재연결
  useEffect(() => {
    if (user && !isConnected) {
      console.log('사용자 로그인 감지, 소켓 재연결 시도');
      setTimeout(() => {
        reconnect?.();
      }, 1000);
    }
  }, [user, isConnected, reconnect]);

  // Ephemeral 세션 ID: 계정별로 sessionStorage 유지 (계정 전환 시 분리)
  // 서버 측 타임아웃(30분)으로 정리. 언마운트 즉시 삭제하지 않음.
  useEffect(() => {
    if (!user?.id) return;
    const STORAGE_KEY = `chatEphemeralSessionId:${user.id}`;
    let id = sessionStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = `eph_${user.id}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      sessionStorage.setItem(STORAGE_KEY, id);
    }
    setEphemeralSessionId(id);
    setEphemeralDocuments([]);

    ephemeralAPI.getSession(id)
      .then((res) => {
        if (res.data?.success) {
          setEphemeralDocuments(res.data.data.documents || []);
        }
      })
      .catch(() => {});
  }, [user?.id]);
  
  // 팀에 속한 사용자(레벨 무관)만 팀 주제방 표시
  const showTeamTopics = (user?.teamIds?.length ?? 0) > 0;
  
  // 팀 주제방 목록 로드
  const loadTeamTopics = async () => {
    if (!showTeamTopics) return;
    
    setLoadingTopics(true);
    try {
      const response = await teamTopicsAPI.getAll();
      setTeamTopics(response.data.data.teams || []);
    } catch (error) {
      // SaaS 모드에서는 팀 API가 없을 수 있음 - 무시
      console.log('Team topics not available (SaaS mode)');
      setTeamTopics([]);
    } finally {
      setLoadingTopics(false);
    }
  };

  // 팀별 세션 목록 로드
  const loadTeamSessions = () => {
    if (sessions.length === 0 || teamTopics.length === 0) return;
    
    // 세션 제목에서 팀 이름 추출하여 그룹화
    const grouped = {};
    
    teamTopics.forEach(team => {
      const teamSessions = sessions.filter(session => {
        // 세션 제목이 [팀이름]으로 시작하는지 확인
        return session.title?.startsWith(`[${team.teamName}]`);
      });
      if (teamSessions.length > 0) {
        grouped[team.teamId] = teamSessions;
      }
    });
    
    setTeamSessions(grouped);
  };
  
  // 새 팀 생성 (팀 주제방 추가)
  const handleCreateTeam = async () => {
    const teamName = window.prompt('새 팀 이름을 입력하세요:');
    if (!teamName?.trim()) return;
    
    try {
      const response = await teamsAPI.create({
        name: teamName.trim()
      });
      
      const newTeam = response.data.data.team;
      
      // 팀 목록 새로고침
      await loadTeamTopics();
      
      setSnackbar({
        open: true,
        message: `"${teamName}" 팀이 생성되었습니다.`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Create team error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || '팀 생성에 실패했습니다.',
        severity: 'error'
      });
    }
  };

  // 새 주제방 생성
  const handleCreateTopic = async (teamId, teamName) => {
    const topicName = window.prompt(`[${teamName}] 새 주제방 이름을 입력하세요:`);
    if (!topicName?.trim()) return;
    
    try {
      const response = await teamTopicsAPI.create({
        teamId,
        name: topicName.trim()
      });
      
      const newTopic = response.data.data.topic;
      
      // 로컬 상태 업데이트
      setTeamTopics(prev => prev.map(team => {
        if (team.teamId === teamId) {
          return {
            ...team,
            topics: [...team.topics, {
              id: newTopic.id,
              name: newTopic.name,
              fileCount: newTopic.fileCount || 0
            }]
          };
        }
        return team;
      }));
      
      // 팀 확장
      setExpandedTeams(prev => ({ ...prev, [teamId]: true }));
      
      setSnackbar({
        open: true,
        message: `[${teamName}] "${topicName}" 주제방이 생성되었습니다.`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Create topic error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || '주제방 생성에 실패했습니다.',
        severity: 'error'
      });
    }
  };
  
  // 주제방 삭제
  const handleDeleteTopic = async (teamId, topicId, topicName) => {
    if (!window.confirm(`"${topicName}" 주제방을 삭제하시겠습니까?`)) return;
    
    try {
      await teamTopicsAPI.delete(topicId);
      
      // 로컬 상태 업데이트
      setTeamTopics(prev => prev.map(team => {
        if (team.teamId === teamId) {
          return {
            ...team,
            topics: team.topics.filter(t => t.id !== topicId)
          };
        }
        return team;
      }));
      
      // 선택된 주제방이 삭제된 경우 선택 해제
      if (selectedTopic?.id === topicId) {
        setSelectedTopic(null);
      }
      
      setSnackbar({
        open: true,
        message: `"${topicName}" 주제방이 삭제되었습니다.`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Delete topic error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || '주제방 삭제에 실패했습니다.',
        severity: 'error'
      });
    }
  };

  // 팀 삭제
  const handleDeleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`"${teamName}" 팀을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) return;
    
    try {
      await teamsAPI.delete(teamId);
      
      // 로컬 상태에서 팀 제거
      setTeamTopics(prev => prev.filter(team => team.teamId !== teamId));
      
      // 선택된 주제방이 삭제된 팀에 속한 경우 선택 해제
      if (selectedTopic && teamTopics.find(t => t.teamId === teamId)?.topics.some(t => t.id === selectedTopic.id)) {
        setSelectedTopic(null);
      }
      
      setSnackbar({
        open: true,
        message: `"${teamName}" 팀이 삭제되었습니다.`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Delete team error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || '팀 삭제에 실패했습니다.',
        severity: 'error'
      });
    }
  };
  
  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const restoreAttemptedRef = useRef(false);
  
  // 팀 선택 (항상 새 대화 시작, 팀 공유 세션으로 생성)
  const handleSelectTeam = async (team) => {
    try {
      // 팀 정보 저장
      setSelectedTeam(team);
      setSelectedTopic(null);
      
      // 항상 새 세션 생성 (teamId 전달로 팀원 모두 공유)
      console.log('🆕 새 팀 세션 생성');
      const response = await ragAPI.createSession({
        title: `[${team.teamName}] 새 대화 ${new Date().toLocaleDateString('ko-KR')}`,
        teamId: team.teamId
      });
      const newSession = response.data.data.session;
      
      // 새 세션을 현재 세션으로 설정
      setCurrentSession(newSession);
      setMessages([]);
      
      // 세션 목록 업데이트
      setSessions(prevSessions => [newSession, ...prevSessions]);
      
      // localStorage에 저장
          rememberSessionId(newSession.id);
          
          // 팀 확장 (세션 목록 보이도록)
          setExpandedTeams(prev => ({ 
            ...prev, 
            [team.teamId]: true 
          }));
          
          setSnackbar({
            open: true,
            message: `[${team.teamName}] 새 대화가 시작되었습니다.`,
            severity: 'success'
          });
        } catch (error) {
          console.error('팀 세션 처리 실패:', error);
      setSnackbar({
        open: true,
        message: '팀 세션 처리에 실패했습니다.',
        severity: 'error'
      });
    }
    
    // 채팅 입력 필드에 포커스
    setTimeout(() => {
      const input = document.querySelector('textarea[placeholder*="메시지"]');
      if (input) {
        input.focus();
      }
    }, 200);
  };

  // 팀 확장/축소 토글
  const handleToggleTeam = (teamId, e) => {
    e.stopPropagation();
    setExpandedTeams(prev => ({
      ...prev,
      [teamId]: !prev[teamId]
    }));
  };
  
  // 주제방 선택 (항상 새 대화 시작, 팀·주제방 공유 세션으로 생성)
  const handleSelectTopic = async (topic, teamName, teamId) => {
    try {
      setSelectedTopic({ ...topic, teamName });
      setSelectedTeam({ teamName, teamId }); // 팀 정보도 설정
      
      // 항상 새 세션 생성 (teamId, topicId 전달로 팀원 모두 공유)
      console.log('🆕 새 주제방 세션 생성');
      const response = await ragAPI.createSession({
        title: `[${teamName}] ${topic.name || '새 대화'} ${new Date().toLocaleDateString('ko-KR')}`,
        teamId: teamId ?? undefined,
        topicId: topic.id ?? undefined
      });
      const newSession = response.data.data.session;
      
      // 새 세션을 현재 세션으로 설정
      setCurrentSession(newSession);
      setMessages([]);
      
      // 세션 목록 업데이트
      setSessions(prevSessions => [newSession, ...prevSessions]);
      
      // localStorage에 저장
      rememberSessionId(newSession.id);
      
      setSnackbar({
        open: true,
        message: `[${teamName}] ${topic.name} 새 대화가 시작되었습니다.`,
        severity: 'success'
      });
    } catch (error) {
      console.error('주제방 세션 처리 실패:', error);
      setSnackbar({
        open: true,
        message: '주제방 세션 처리에 실패했습니다.',
        severity: 'error'
      });
    }
  };

  // 계정 전환 시 채팅 상태 초기화 후 재로드
  useEffect(() => {
    restoreAttemptedRef.current = false;
    setCurrentSession(null);
    setMessages([]);
    setSessions([]);
    setSelectedTeam(null);
    setSelectedTopic(null);
    setChartData(null);
    loadSessions();
    loadTeamTopics();
  }, [user?.id]);

  // 세션이 로드되면 팀별 세션 그룹화
  useEffect(() => {
    if (sessions.length > 0 && teamTopics.length > 0) {
      loadTeamSessions();
    }
  }, [sessions, teamTopics]);

  // 페이지 복귀 시 마지막 세션 자동 복원 (1회)
  useEffect(() => {
    const restoreLastSession = async () => {
      // 팀이 선택되었거나 주제방이 선택되었으면 세션 복원하지 않음
      if (selectedTeam || selectedTopic) {
        if (currentSession) {
          setCurrentSession(null);
          setMessages([]);
        }
        return;
      }

      if (restoreAttemptedRef.current) return;
      
      // 세션 목록이 로드되었고, 현재 세션이 없을 때만 복원
      if (sessions.length > 0 && !currentSession && !loadingSession) {
        restoreAttemptedRef.current = true;
        const lastSessionId = readRememberedSessionId();
        
        if (lastSessionId) {
          const lastSession = sessions.find(s => String(s.id) === String(lastSessionId));
          if (lastSession) {
            console.log('🔄 마지막 세션 복원:', lastSession.title);
            await handleSelectSession(lastSession, { silent: true });
            return;
          }
          // 목록에 없으면(타 계정·삭제된 세션) 기억 키만 정리
          clearRememberedSessionId();
        }
        
        // 자동 복원 실패/부재 시 빈 화면 유지 — 사용자가 새 대화를 시작하면 됨
      }
    };

    // 약간의 지연을 두어 중복 실행 방지
    const timeoutId = setTimeout(() => {
      restoreLastSession();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [sessions, currentSession, selectedTopic, selectedTeam, loadingSession]); // selectedTeam 의존성 추가

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 소켓 이벤트 핸들러 설정
  useEffect(() => {
    console.log('🔌 소켓 이벤트 핸들러 설정:', { isConnected });
    
    if (!isConnected) {
      console.log('소켓이 연결되지 않았습니다. RAG API를 사용합니다.');
      return;
    }

    console.log('✅ 소켓 연결됨, 이벤트 핸들러 등록 중...');

    // 스트리밍 청크 처리
    const unsubscribeChunk = onChunk(({ content, done }) => {
      if (content) setProcessingStatus('');
      if (!done && content) {
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          
          if (lastMessage && lastMessage.role === 'assistant' && lastMessage.isStreaming) {
            lastMessage.content += content;
          } else {
            newMessages.push({
              role: 'assistant',
              content,
              isStreaming: true,
              timestamp: new Date()
            });
          }
          
          return newMessages;
        });
      }
    });

    // 완료 처리
    const unsubscribeComplete = onComplete(({ content, sources, responseTimeMs, hasChart, sessionId: respSessionId }) => {
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        
        if (lastMessage && lastMessage.role === 'assistant' && lastMessage.isStreaming) {
          lastMessage.content = content;
          lastMessage.isStreaming = false;
          lastMessage.sources = sources;
          lastMessage.responseTimeMs = responseTimeMs;
          lastMessage.hasChart = hasChart;
        } else {
          newMessages.push({
            role: 'assistant',
            content,
            sources,
            responseTimeMs,
            hasChart,
            timestamp: new Date()
          });
        }
        
        return newMessages;
      });
      
      // 세션 ID가 반환되면 세션 목록 갱신
      if (respSessionId) {
        rememberSessionId(respSessionId);
        loadSessions();
      }
      
      setIsLoading(false);
      setProcessingStatus('');
    });

    // 차트 데이터 처리
    const unsubscribeChart = onChart((data) => {
      console.log('📊 차트 데이터 수신:', data);
      setChartData(data);
      // 차트 데이터를 마지막 assistant 메시지에 직접 포함
      setMessages(prev => {
        const newMessages = [...prev];
        const lastAssistant = [...newMessages].reverse().find(m => m.role === 'assistant');
        if (lastAssistant) {
          lastAssistant.chartData = data;
          lastAssistant.hasChart = true;
        }
        return newMessages;
      });
    });

    // 오류 처리
    const unsubscribeError = onError(({ message }) => {
      setSnackbar({
        open: true,
        message: `오류: ${message}`,
        severity: 'error'
      });
      setIsLoading(false);
      setProcessingStatus('');
    });

    // 일일 채팅 제한 처리
    const unsubscribeLimit = onLimit(({ type, message, remaining, dailyLimit, used }) => {
      if (type === 'exceeded') {
        // 제한 초과 - 메시지로 표시
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `⚠️ **일일 채팅 제한 초과**\n\n${message}\n\n[스탠다드 플랜 업그레이드](/mypage)를 통해 무제한으로 이용하세요.`,
          timestamp: new Date(),
          isLimitMessage: true
        }]);
        setIsLoading(false);
        setProcessingStatus('');
      } else if (type === 'warning') {
        // 잔여 횟수 경고
        setSnackbar({
          open: true,
          message: `⚠️ ${message}`,
          severity: 'warning'
        });
      }
    });

    const unsubscribeStatus = onStatus(({ message }) => {
      if (message) setProcessingStatus(message);
    });

    return () => {
      unsubscribeChunk();
      unsubscribeComplete();
      unsubscribeChart();
      unsubscribeError();
      unsubscribeLimit();
      unsubscribeStatus();
    };
  }, [isConnected, onChunk, onComplete, onChart, onError, onLimit, onStatus]);

  const loadSessions = async () => {
    try {
      const response = await ragAPI.getSessions({ limit: 50 });
      setSessions(response.data.data.sessions || []);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  // 일반 세션 목록 (팀 세션 제외)
  const getGeneralSessions = () => {
    if (teamTopics.length === 0) return sessions;
    
    // 팀 이름 목록 생성
    const teamNames = teamTopics.map(team => team.teamName);
    
    // 팀 이름으로 시작하지 않는 세션만 반환
    return sessions.filter(session => {
      if (!session.title) return true;
      // [팀이름]으로 시작하는 세션은 제외
      return !teamNames.some(teamName => session.title.startsWith(`[${teamName}]`));
    });
  };

  // 특정 팀의 세션 목록 가져오기
  const getTeamSessions = (teamName) => {
    return sessions.filter(session => {
      return session.title && session.title.startsWith(`[${teamName}]`);
    });
  };

  // 특정 주제방의 세션 목록 가져오기 (향후 확장 가능)
  const getTopicSessions = (teamName, topicName) => {
    return sessions.filter(session => {
      return session.title && session.title.includes(`[${teamName}]`) && 
             session.title.includes(topicName);
    });
  };

  const handleCreateSession = async () => {
    try {
      // 팀/주제방 선택 해제
      setSelectedTeam(null);
      setSelectedTopic(null);
      
      const response = await ragAPI.createSession({
        title: `새 대화 ${new Date().toLocaleDateString('ko-KR')}`
      });
      const newSession = response.data.data.session;
      setSessions([newSession, ...sessions]);
      setCurrentSession(newSession);
      setMessages([]);
      rememberSessionId(newSession.id);
    } catch (error) {
      setSnackbar({
        open: true,
        message: '세션 생성에 실패했습니다.',
        severity: 'error'
      });
    }
  };

  // 팀용 새 대화 생성 (팀 공유 세션)
  const handleCreateTeamSession = async (teamName, teamId) => {
    try {
      const response = await ragAPI.createSession({
        title: `[${teamName}] 새 대화 ${new Date().toLocaleDateString('ko-KR')}`,
        teamId: teamId ?? undefined
      });
      const newSession = response.data.data.session;
      
      setSessions([newSession, ...sessions]);
      setCurrentSession(newSession);
      setMessages([]);
      rememberSessionId(newSession.id);
      
      setSnackbar({
        open: true,
        message: `[${teamName}] 새 대화가 생성되었습니다.`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: '팀 세션 생성에 실패했습니다.',
        severity: 'error'
      });
    }
  };

  const handleSelectSession = async (session, { silent = false } = {}) => {
    // 팀/주제방 선택 해제 (먼저 해제하여 자동 복원 방지)
    setSelectedTeam(null);
    setSelectedTopic(null);
    
    setLoadingSession(true);
    try {
      const response = await ragAPI.getSession(session.id);
      const sessionData = response.data.data.session;
      setCurrentSession(sessionData);
      setMessages(sessionData.messages || []);
      // localStorage에 마지막 세션 ID 저장
      rememberSessionId(session.id);
    } catch (error) {
      const status = error.response?.status;
      // 접근 불가·삭제된·손상된 세션은 목록과 기억에서 제거
      clearRememberedSessionId();
      setSessions((prev) => prev.filter((s) => s.id !== session.id));
      if (currentSession?.id === session.id) {
        setCurrentSession(null);
        setMessages([]);
      }
      if (!silent) {
        const msg = status === 403
          ? '이 대화에 접근할 수 없습니다.'
          : status === 404
            ? '대화를 찾을 수 없습니다. 목록에서 제거했습니다.'
            : '세션을 불러오는데 실패했습니다.';
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    } finally {
      setLoadingSession(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await ragAPI.deleteSession(sessionId);
      setSessions(sessions.filter(s => s.id !== sessionId));
      if (currentSession?.id === sessionId) {
        setCurrentSession(null);
        setMessages([]);
      }
      // 팀별 세션 목록도 업데이트
      loadTeamSessions();
    } catch (error) {
      setSnackbar({
        open: true,
        message: '세션 삭제에 실패했습니다.',
        severity: 'error'
      });
    }
  };

  // 파일 첨부 핸들러 (+ 버튼 클릭 시) — 비동기 job 폴링 + 진행 상태
  const handleAttachFiles = async (files) => {
    if (!files || files.length === 0 || !ephemeralSessionId) return;
    
    setUploadingFiles(true);
    setProcessingStatus('파일 업로드 중…');
    try {
      const response = await ephemeralAPI.upload(files, ephemeralSessionId, {
        onProgress: (job) => {
          setProcessingStatus(job.message || '문서 처리 중…');
        }
      });
      const data = response.data;
      
      if (data.success) {
        // 세션 정보 갱신
        const sessionInfo = await ephemeralAPI.getSession(ephemeralSessionId);
        setEphemeralDocuments(sessionInfo.data.data.documents || []);
        
        const successCount = (data.results || []).filter(r => r.success).length;
        setSnackbar({
          open: true,
          message: data.truncated
            ? `${successCount}개 파일 준비됨 (체험/속도용으로 일부만 인덱싱). 원본은 삭제됨.`
            : `${successCount}개 파일이 준비되었습니다. (처리 후 원본 삭제됨)`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: data.error || '파일 처리에 실패했습니다.',
          severity: 'error'
        });
      }
    } catch (error) {
      let message = '파일 업로드 중 오류가 발생했습니다.';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.error) message = parsed.error;
        } catch (_) { /* ignore */ }
      } else if (error.message) {
        message = error.message;
      }
      setSnackbar({
        open: true,
        message,
        severity: 'error'
      });
    } finally {
      setUploadingFiles(false);
      setProcessingStatus('');
    }
  };

  // Ephemeral 문서 업로드 완료 콜백
  const handleEphemeralUploadComplete = async (results) => {
    if (results) {
      // 결과가 전달된 경우 (업로드 완료)
      const sessionInfo = await ephemeralAPI.getSession(ephemeralSessionId);
      setEphemeralDocuments(sessionInfo.data.data.documents || []);
    } else {
      // 문서 삭제 후 갱신
      const sessionInfo = await ephemeralAPI.getSession(ephemeralSessionId);
      setEphemeralDocuments(sessionInfo.data.data.documents || []);
    }
  };

  const handleSend = async (message) => {
    // 사용자 메시지 즉시 추가
    const userMessage = { role: 'user', content: message, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setProcessingStatus('요청 처리 중…');
    setChartData(null); // 이전 차트 데이터 초기화

    console.log('💬 메시지 전송:', { message, isConnected, currentSession });

    // 소켓 연결 상태에 따라 분기 처리
    if (isConnected) {
      console.log('🔌 소켓 사용하여 메시지 전송');
      
      // 세션이 없으면 먼저 생성
      let activeSessionId = currentSession?.id;
      if (!activeSessionId) {
        try {
          const response = await ragAPI.createSession({
            title: `새 대화 ${new Date().toLocaleDateString('ko-KR')}`
          });
          const newSession = response.data.data.session;
          setSessions(prev => [newSession, ...prev]);
          setCurrentSession(newSession);
          activeSessionId = newSession.id;
          rememberSessionId(newSession.id);
        } catch (err) {
          console.error('세션 생성 실패:', err);
        }
      }
      
      // 소켓 사용
      if (activeSessionId) {
        joinSession(activeSessionId);
      }

      sendMessage(message, activeSessionId, {
        fileIds: null,
        ephemeralSessionId: ephemeralDocuments.length > 0 ? ephemeralSessionId : undefined
      });
    } else {
      console.log('🌐 RAG API 직접 사용');
      // RAG API 사용 (세션/메시지 DB 저장 유지)
      try {
        let sessionTitle = null;
        if (!currentSession) {
          sessionTitle = `새 대화 ${new Date().toLocaleDateString('ko-KR')}`;
        }
        
        const response = await ragAPI.query(message, {
          sessionId: currentSession?.id,
          title: sessionTitle,
          ephemeralSessionId: ephemeralDocuments.length > 0 ? ephemeralSessionId : null
        });

        const data = response.data.data;
        
        // AI 응답 추가
        const aiMessage = {
          role: 'assistant',
          content: data.answer,
          sources: data.sources || [],
          responseTimeMs: data.responseTimeMs,
          model: data.model,
          timestamp: new Date(),
        };
        
        setMessages(prev => [...prev, aiMessage]);

        // 세션 업데이트
        if (data.sessionId) {
          if (!currentSession || currentSession.id !== data.sessionId) {
            await loadSessions();
            try {
              const sessionResponse = await ragAPI.getSession(data.sessionId);
              const newSessionData = sessionResponse.data.data.session;
              setCurrentSession(newSessionData);
              rememberSessionId(data.sessionId);
            } catch (err) {
              console.error('Failed to load new session:', err);
            }
          } else {
            loadSessions();
          }
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.response?.data?.error || '응답 생성에 실패했습니다.',
          severity: 'error'
        });
        
        // 오류 시 사용자 메시지 제거
        setMessages(prev => prev.slice(0, -1));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCopyMessage = () => {
    setSnackbar({
      open: true,
      message: '클립보드에 복사되었습니다.',
      severity: 'success'
    });
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 128px)' }}>
      {/* Sidebar - 모바일에서 숨김 */}
      <Paper sx={{ 
        width: 280, 
        flexShrink: 0, 
        mr: 2, 
        display: { xs: 'none', md: 'flex' }, 
        flexDirection: 'column', 
        overflow: 'hidden' 
      }}>
        {/* 새 대화 및 세션 목록 */}
        <Box sx={{ flexShrink: 0 }}>
          <SessionList
            sessions={getGeneralSessions()}
            currentSessionId={currentSession?.id}
            onSelect={handleSelectSession}
            onCreate={handleCreateSession}
            onDelete={handleDeleteSession}
            loading={loadingSession}
          />
        </Box>
        
        {/* 팀 주제방 (Level2 이상만 표시) */}
        {showTeamTopics && (
          <Box sx={{ flexGrow: 1, overflow: 'auto', borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                  팀 주제방
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                onClick={handleCreateTeam}
                sx={{ width: 24, height: 24 }}
                title="새 팀 생성"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ px: 1.5, pb: 1 }}>
              {teamTopics.map((team) => (
                <Box key={team.teamId} sx={{ mb: 1 }}>
                  {/* 팀 주제방 버튼 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleSelectTeam(team)}
                      sx={{
                        flexGrow: 1,
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        borderStyle: 'dashed',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                          borderStyle: 'solid',
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      {team.teamName} 주제방
                    </Button>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTeam(team.teamId, team.teamName);
                      }}
                      sx={{ width: 24, height: 24 }}
                      color="error"
                      title="팀 삭제"
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  {/* 팀별 세션 목록 */}
                  <Collapse in={expandedTeams[team.teamId]} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 2 }}>
                      {teamSessions[team.teamId] && teamSessions[team.teamId].length > 0 ? (
                        teamSessions[team.teamId].map((session) => (
                          <Paper
                            key={session.id}
                            elevation={0}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: 1,
                              mb: 0.5,
                              bgcolor: 'grey.50',
                              borderRadius: 1,
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: 'grey.100'
                              },
                              ...(currentSession?.id === session.id && {
                                bgcolor: 'primary.light',
                                '&:hover': {
                                  bgcolor: 'primary.light'
                                }
                              })
                            }}
                            onClick={() => handleSelectSession(session)}
                          >
                            <BotIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography variant="body2" noWrap>
                                {session.title?.replace(`[${team.teamName}] `, '') || '새 대화'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {session.updated_at 
                                  ? `${Math.floor((Date.now() - new Date(session.updated_at).getTime()) / (1000 * 60 * 60))}시간 전`
                                  : ''}
                              </Typography>
                            </Box>
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSession(session.id);
                              }}
                              sx={{ width: 24, height: 24, ml: 0.5 }}
                              title="대화 삭제"
                            >
                              <RemoveIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Paper>
                        ))
                      ) : (
                        expandedTeams[team.teamId] && (
                          <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              아직 대화가 없습니다.
                            </Typography>
                          </Box>
                        )
                      )}
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </Box>
            
            {teamTopics.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  소속된 팀이 없습니다.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Chat Area */}
      <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Messages */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
          {loadingSession ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : messages.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              <BotIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6">KOAI AI 채팅</Typography>
              {selectedTeam ? (
                <>
                  <Chip 
                    label={`[${selectedTeam.teamName}] 팀 채팅`}
                    color="primary"
                    sx={{ mt: 1, mb: 1 }}
                  />
                  <Typography variant="body2">
                    {selectedTeam.teamName} 팀과의 대화입니다.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    질문을 입력하면 AI가 답변해 드립니다.
                  </Typography>
                </>
              ) : selectedTopic ? (
                <>
                  <Chip 
                    label={`[${selectedTopic.teamName}] ${selectedTopic.name}`}
                    color="primary"
                    sx={{ mt: 1, mb: 1 }}
                  />
                  <Typography variant="body2">
                    이 주제방의 문서를 기반으로 AI가 답변해 드립니다.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    등록된 파일: {selectedTopic.fileCount}개
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2">
                    질문을 입력하면 AI가 답변해 드립니다.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    📎 + 버튼으로 문서를 첨부하면 AI가 내용을 분석하여 답변합니다.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    파일은 분석 후 즉시 삭제되며, 서버에 저장되지 않습니다.
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            <>
              {messages.map((msg, index) => {
                let questionText = '';
                if (msg.role === 'assistant') {
                  for (let i = index - 1; i >= 0; i -= 1) {
                    if (messages[i].role === 'user') {
                      questionText = messages[i].content;
                      break;
                    }
                  }
                }
                return (
                  <ChatMessage
                    key={index}
                    message={msg}
                    questionText={questionText}
                    onCopy={handleCopyMessage}
                    onExportError={(message) => setSnackbar({ open: true, message, severity: 'error' })}
                    chartData={msg.chartData || (msg.hasChart && index === messages.length - 1 ? chartData : null)}
                  />
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </Box>

        {/* Input */}
        <Divider />
        {/* 연결 상태 표시 */}
        {!isConnected && (
          <Box sx={{ p: 1, bgcolor: 'info.light', textAlign: 'center' }}>
            <Typography variant="caption" color="info.dark">
              💡 실시간 연결 없음 - 기본 채팅 모드로 작동 중 (차트 기능 제한됨)
            </Typography>
          </Box>
        )}
        {isConnected && (
          <Box sx={{ p: 0.5, bgcolor: 'success.light', textAlign: 'center' }}>
            <Typography variant="caption" color="success.dark">
              ✅ 실시간 연결됨 - 모든 기능 사용 가능
            </Typography>
          </Box>
        )}
        {/* Ephemeral 파일 업로드 영역 */}
        {ephemeralDocuments.length > 0 && (
          <EphemeralFileUpload
            sessionId={ephemeralSessionId}
            onUploadComplete={handleEphemeralUploadComplete}
            documents={ephemeralDocuments}
          />
        )}
        {(uploadingFiles || processingStatus) && (
          <Box sx={{ px: 2, pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {processingStatus || '처리 중…'}
            </Typography>
          </Box>
        )}
        <ChatInput
          onSend={handleSend}
          onAttach={handleAttachFiles}
          isLoading={isLoading || isStreaming || uploadingFiles}
          disabled={loadingSession}
        />
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
